import { ChainId, Token } from '@uniswap/sdk'
import { Tags, TokenInfo, TokenList } from '@uniswap/token-lists'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from '../index'

type TagDetails = Tags[keyof Tags]
export interface TagInfo extends TagDetails {
  id: string
}

/**
 * Token instances created from token info.
 */
export class WrappedTokenInfo extends Token {
  public readonly tokenInfo: TokenInfo
  public readonly tags: TagInfo[]
  constructor(tokenInfo: TokenInfo, tags: TagInfo[]) {
    super(tokenInfo.chainId, tokenInfo.address, tokenInfo.decimals, tokenInfo.symbol, tokenInfo.name)
    this.tokenInfo = tokenInfo
    this.tags = tags
  }
  public get logoURI(): string | undefined {
    return this.tokenInfo.logoURI
  }
}

export type TokenAddressMap = Readonly<{ [chainId in ChainId]: Readonly<{ [tokenAddress: string]: WrappedTokenInfo }> }>

/**
 * An empty result, useful as a default.
 */
const EMPTY_LIST: TokenAddressMap = {
  [ChainId.MUMBAI]: {},
  [ChainId.MATIC]: {}
}

const listCache: WeakMap<TokenList, TokenAddressMap> | null =
  typeof WeakMap !== 'undefined' ? new WeakMap<TokenList, TokenAddressMap>() : null

export function listToTokenMap(list: TokenList): TokenAddressMap {
  const result = listCache?.get(list)
  if (result) return result
  // let allTokens;
  // return allTokens = {
  //   8453: {
  //     "0x714550C2C1Ea08688607D86ed8EeF4f5E4F22323": {
  //       "decimals": 18,
  //       "symbol": "ETH",
  //       "name": "Ether",
  //       "chainId": 80001,
  //       "address": "0x714550C2C1Ea08688607D86ed8EeF4f5E4F22323",
  //       "tokenInfo": {
  //         "name": "Ether",
  //         "address": "0x714550C2C1Ea08688607D86ed8EeF4f5E4F22323",
  //         "symbol": "ETH",
  //         "decimals": 18,
  //         "chainId": 80001,
  //         "logoURI": "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2/logo.png"
  //       },
  //       "tags": []
  //     },
  //   },
  //   84532: {
  //     "0x714550C2C1Ea08688607D86ed8EeF4f5E4F22323": {
  //       "decimals": 6,
  //       "symbol": "USDT",
  //       "name": "USDT",
  //       "chainId": 80001,
  //       "address": "0x20555ad39b57A1D642ed1BE8625571eE93d5bE43",
  //       "tokenInfo": {
  //         "name": "USDT",
  //         "address": "0x714550C2C1Ea08688607D86ed8EeF4f5E4F22323",
  //         "symbol": "USDT",
  //         "decimals": 6,
  //         "chainId": 80001,
  //         "logoURI": "https://cryptologos.cc/logos/tether-usdt-logo.png?v=031"
  //       },
  //       "tags": []
  //     },
  //   }
  // }
  const map = list.tokens.reduce<TokenAddressMap>(
    (tokenMap, tokenInfo) => {
      const tags: TagInfo[] =
        tokenInfo.tags
          ?.map(tagId => {
            if (!list.tags?.[tagId]) return undefined
            return { ...list.tags[tagId], id: tagId }
          })
          ?.filter((x): x is TagInfo => Boolean(x)) ?? []
      const token = new WrappedTokenInfo(tokenInfo, tags)
      if (tokenMap[token.chainId][token.address] !== undefined) throw Error('Duplicate tokens.')
      return {
        ...tokenMap,
        [token.chainId]: {
          ...tokenMap[token.chainId],
          [token.address]: token
        }
      }
    },
    { ...EMPTY_LIST }
  )
  listCache?.set(list, map);
  return map
}

export function useTokenList(url: string | undefined): TokenAddressMap {
  const lists = useSelector<AppState, AppState['lists']['byUrl']>(state => state.lists.byUrl)
  return useMemo(() => {
    if (!url) return EMPTY_LIST
    const current = lists[url]?.current
    if (!current) return EMPTY_LIST
    try {
      let data = {
        "name": "baseswap Token List",
        "timestamp": "2021-07-29T04:18:23.593Z",
        "version": {
          "major": 1,
          "minor": 0,
          "patch": 82
        },
        "tags": {},
        "logoURI": "ipfs://QmQ9GCVmLQkbPohxKeCYkbpmwfTvHXrY64TmBsPQAZdbqZ",
        "keywords": [
          "uniswap",
          "default"
        ],
        "tokens": [
          {
            "name": "Tether USD",
            "address": "0x85Bb9f34d0919014D91FDfA97D6A9779087F32bE",
            "symbol": "USDT",
            "decimals": 6,
            "chainId": 84532,
            "logoURI": "https://cryptologos.cc/logos/tether-usdt-logo.png?v=031"
          }
        ]
      }
      return listToTokenMap(data);


    } catch (error) {
      console.error('Could not show token list due to error', error)
      return EMPTY_LIST
    }
  }, [lists, url])
}

export function useSelectedListUrl(): string | undefined {
  return useSelector<AppState, AppState['lists']['selectedListUrl']>(state => state.lists.selectedListUrl)
}

export function useSelectedTokenList(): TokenAddressMap {
  return useTokenList(useSelectedListUrl())
}

export function useSelectedListInfo(): { current: TokenList | null; pending: TokenList | null; loading: boolean } {
  const selectedUrl = useSelectedListUrl()
  const listsByUrl = useSelector<AppState, AppState['lists']['byUrl']>(state => state.lists.byUrl)
  let list = selectedUrl ? listsByUrl[selectedUrl] : undefined

  return {
    current: list?.current ?? null,
    pending: list?.pendingUpdate ?? null,
    loading: list?.loadingRequestId !== null
  }
}

// returns all downloaded current lists
export function useAllLists(): TokenList[] {
  const lists = useSelector<AppState, AppState['lists']['byUrl']>(state => state.lists.byUrl)

  return useMemo(
    () =>
      Object.keys(lists)
        .map(url => lists[url].current)
        .filter((l): l is TokenList => Boolean(l)),
    [lists]
  )
}
